export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly signal?: AbortSignal;
}

export interface ApiRequestInit {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  readonly path: string;
  readonly body?: unknown;
  readonly idempotencyKey?: string;
}

export type ApiResult<T> =
  | { readonly kind: 'ok'; readonly status: number; readonly data: T }
  | {
      readonly kind: 'error';
      readonly status: number;
      readonly reason: string;
      readonly body: Record<string, unknown> | null;
    }
  | { readonly kind: 'auth_required'; readonly status: number }
  | { readonly kind: 'forbidden'; readonly missingCapability: string | null }
  | { readonly kind: 'network_error'; readonly message: string };

export function resolveBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_ADMIN_API_BASE_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) {
    return fromEnv.replace(/\/$/, '');
  }
  return '';
}

export const ADMIN_READ_TIMEOUT_MS = 12_000;

export async function callAdminApi<T>(
  options: ApiClientOptions,
  init: ApiRequestInit,
): Promise<ApiResult<T>> {
  // Only reads receive a deadline. Operator mutations retain their existing
  // outcome semantics and are never retried or timed out by this wrapper.
  if ((init.method ?? 'GET') !== 'GET') return performAdminApi<T>(options, init);
  const controller = new AbortController();
  const cancel = () => controller.abort();
  if (options.signal?.aborted) cancel();
  options.signal?.addEventListener('abort', cancel, { once: true });
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<ApiResult<T>>(resolve => {
    timer = setTimeout(() => {
      resolve({ kind: 'network_error', message: 'request_timeout' });
      controller.abort();
    }, ADMIN_READ_TIMEOUT_MS);
  });
  try {
    return await Promise.race([performAdminApi<T>({ ...options, signal: controller.signal }, init), deadline]);
  } finally {
    clearTimeout(timer!);
    options.signal?.removeEventListener('abort', cancel);
  }
}

async function performAdminApi<T>(
  options: ApiClientOptions,
  init: ApiRequestInit,
): Promise<ApiResult<T>> {
  const method = init.method ?? 'GET';
  const url = `${options.baseUrl}${init.path}`;
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: BodyInit | undefined;
  if (init.body !== undefined && method !== 'GET') {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(init.body);
  }
  if (init.idempotencyKey) {
    headers['idempotency-key'] = init.idempotencyKey;
  }
  let resp: Response;
  try {
    resp = await fetch(url, {
      method,
      headers,
      body,
      credentials: 'include',
      signal: options.signal ?? null,
    });
  } catch (err) {
    return {
      kind: 'network_error',
      message: err instanceof Error ? err.message : String(err),
    };
  }
  const status = resp.status;
  let parsed: Record<string, unknown> | null = null;
  try {
    const text = await resp.text();
    parsed = text.length > 0 ? (JSON.parse(text) as Record<string, unknown>) : null;
  } catch {
    parsed = null;
  }
  if (status === 401) return { kind: 'auth_required', status };
  if (status === 403) {
    const missing = typeof parsed?.missingCapability === 'string' ? parsed.missingCapability : null;
    return { kind: 'forbidden', missingCapability: missing };
  }
  if (status >= 200 && status < 300) {
    // Phase 22 R22.1 — 200+empty body veya 200+`null` body operatör
    // ekranında "Cannot read properties of null (reading X)" tipi
    // TypeError'a yol açıyordu. Burada error olarak işleyip page'in
    // ErrorState'i göstermesini sağlıyoruz; downstream tarafında null
    // guard'a güvenmek yerine erkenden kesiyoruz.
    if (parsed === null || parsed === undefined) {
      return {
        kind: 'error',
        status,
        reason: 'empty_response_body',
        body: null,
      };
    }
    return { kind: 'ok', status, data: (parsed as unknown) as T };
  }
  const reasonRaw = parsed?.reason;
  const reason = typeof reasonRaw === 'string' ? reasonRaw : `http_${status}`;
  return { kind: 'error', status, reason, body: parsed };
}
